<template>
  <div>

    <Head :title="title" />
    <div class="max-w-full bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.first_name" :error="form.errors.first_name" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('First name')" />
          <text-input v-model="form.last_name" :error="form.errors.last_name" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('Last name')" />
          <text-input v-model="form.email" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('Email')" readonly />
          <text-input v-model="form.phone" :error="form.errors.phone" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('Phone')" />
          <text-input v-model="form.city" :error="form.errors.city" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('City')" />
          <text-input v-model="form.address" :error="form.errors.address" class="pb-8 pr-6 w-full lg:w-1/3"
            :label="__('Address')" />
          <select-input v-model="form.country_id" :error="form.errors.country_id" class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Country')">
            <option :value="null" />
            <option v-for="c in countries" :key="c.id" :value="c.id">{{ __(c.name) }}</option>
          </select-input>
          <select-input v-model="form.campus" :error="form.errors.campus" required class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Campus')">
            <option :value="null">{{ __('Select Campus') }}</option>
            <option v-for="c in availableCampusOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
          </select-input>
          <select-input v-if="form.campus === 'online'" v-model="form.awarding_body" :error="form.errors.awarding_body"
            required class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Awarding Body')">
            <option :value="null">{{ __('Select Awarding Body') }}</option>
            <option v-for="a in availableAwardingBodyOptions" :key="a.value" :value="a.value">{{ a.label }}</option>
          </select-input>
          <select-input v-model="form.class_id" :error="form.errors.class_id" class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Class')">
            <option :value="null">{{ __('Select Class') }}</option>
            <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select-input>
          <select-input v-model="form.student_category_id" :error="form.errors.student_category_id" class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Category')">
            <option :value="null">{{ __('Select Category') }}</option>
            <option v-for="cat in studentCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select-input>
          <!-- <text-input v-model="form.password" :error="form.errors.password" class="pb-8 pr-6 w-full lg:w-1/3" type="password" autocomplete="new-password" :label="__('Password')" /> -->
          <div class="password-input-container w-full lg:w-1/3">
            <text-input v-model="form.password" :type="passwordFieldType" :error="form.errors.password"
              label="Password" />
            <i :class="passwordFieldType === 'password' ? 'fa fa-eye' : 'fa fa-eye-slash'" class="toggle-password-icon"
              @click="togglePasswordVisibility"></i>
          </div>
          <file-input v-model="form.photo_path" :error="form.errors.photo_path" class="pb-8 pr-6 w-full lg:w-1/3"
            type="file" accept="image/*" label="Photo" />
          <div class="w-full lg:w-1/3 flex items-center justify-start"><img v-if="user.photo_path"
              class="block mb-2 w-8 h-8 rounded-full" :src="user.photo_path" /></div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="user.id !== auth.user.id && user_access.customer.delete" class="text-red-600 hover:underline"
            tabindex="-1" type="button" @click="destroy">
            {{ __('Delete') }}</button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">{{ __('Update')
            }}</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
  },
  layout: Layout,
  props: {
    user: Object,
    auth: Object,
    countries: Array,
    cities: Array,
    classes: Array,
    studentCategories: Array,
    campusOptions: Array,
    awardingBodyOptions: Array,
    title: String,
  },
  remember: 'form',
  data() {
    return {
      showPassword: false,
      user_access: this.$page.props.auth.user.access,
      form: this.$inertia.form({
        _method: 'put',
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
        phone: this.user.phone,
        city: this.user.city,
        address: this.user.address,
        country_id: this.user.country_id,
        campus: this.user.campus,
        awarding_body: this.user.awarding_body,
        class_id: this.user.class_id,
        student_category_id: this.user.student_category_id,
        password: '',
        photo_path: null
      }),
    }
  },
  created() {
    // this.setDefaultValue(this.countries, 'country_id', 'United States')
  },
  computed: {
    passwordFieldType() {
      return this.showPassword ? 'text' : 'password';
    },
    // Keeps a student's existing campus/awarding body visible in the dropdown even if it has
    // since been retired from the canonical list, so opening this form doesn't blank it out.
    availableCampusOptions() {
      return this.withLegacyOption(this.campusOptions, this.user.campus)
    },
    availableAwardingBodyOptions() {
      return this.withLegacyOption(this.awardingBodyOptions, this.user.awarding_body)
    },
  },
  watch: {
    'form.campus'(value) {
      if (value !== 'online') {
        this.form.awarding_body = null
      }
    },
  },
  methods: {
    withLegacyOption(options, currentValue) {
      if (!currentValue || options.some(o => o.value === currentValue)) {
        return options
      }
      return [...options, { value: currentValue, label: currentValue }]
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    setDefaultValue(arr, key, value) {
      const find = arr.find(i => i.name.match(new RegExp(value + ".*")))
      if (find) {
        this.form[key] = find['id']
      }
    },
    update() {
      this.form.post(this.route('customers.update', this.user.id), {
        onSuccess: () => this.form.reset('password', 'photo'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this user?')) {
        this.$inertia.delete(this.route('customers.destroy', this.user.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this user?')) {
        this.$inertia.put(this.route('customers.restore', this.user.id))
      }
    },
  },
}
</script>
<style>
.password-input-container {
  margin-right: 10px;
  position: relative;
  /* display: flex; */
  /* align-items: center; */
}

.toggle-password-icon {
  position: absolute;
  right: 10px;
  top: 38%;
  cursor: pointer;
}
</style>
